import type { FeatureOption } from '../../../../../components/FeatureSelector/FeatureSelector'

import { Stack, Typography, useTheme } from '@mui/material'
import { type SVGProps } from 'react'

import { FeatureCard } from '../../../../../components/FeatureSelector/FeatureCard'
import {
  RepeatBgIcon,
  SpokenOpenResponseBgIcon,
  WrittenOpenResponseBgIcon,
  MultipleChoiceBgIcon,
  ShortAnswerBgIcon,
} from '../../../../../components/FeatureSelector/FeatureSelector'
import { CardActivityType } from '../../../../../models/card-activity/card-activity.model'

// Constants for the standard feature colors
const FEATURE_COLORS = {
  [CardActivityType.READ_RESPOND]: '#528BFF', // Spoken Open Response
  [CardActivityType.RESPOND_WRITE]: 'rgb(134, 203, 60)', // Written Open Response
  [CardActivityType.SHORT_ANSWER]: '#3CCB7F', // Short Answer
  [CardActivityType.READ_REPEAT]: '#2CE', // Listen & Repeat
  [CardActivityType.MULTIPLE_CHOICE]: '#7A5AF8', // Multiple Choice - purple color from CardTypeChip
}

// Map bg icons to each feature type
const FEATURE_BG_ICONS = {
  [CardActivityType.READ_RESPOND]: <SpokenOpenResponseBgIcon />,
  [CardActivityType.RESPOND_WRITE]: <WrittenOpenResponseBgIcon />,
  [CardActivityType.READ_REPEAT]: <RepeatBgIcon />,
  [CardActivityType.MULTIPLE_CHOICE]: <MultipleChoiceBgIcon />,
  [CardActivityType.SHORT_ANSWER]: <ShortAnswerBgIcon />,
}

export function CardSelectorCardType({
  card,
  onClick,
  selected,
}: {
  card: {
    title: string
    description: string
    icon: JSX.Element
    bgcolor: string
    type?: CardActivityType | 'BULK_CREATE'
  }
  selected: boolean
  onClick: () => void
}) {
  // Determine the appropriate ID and color based on card title or explicit type
  const cardType = card.type || getCardTypeFromTitle(card.title)
  const cardColor = FEATURE_COLORS[cardType as keyof typeof FEATURE_COLORS] || card.bgcolor
  const bgIcon = FEATURE_BG_ICONS[cardType as keyof typeof FEATURE_BG_ICONS] || <></>

  // Transform the card props to match FeatureOption format
  const featureOption: FeatureOption = {
    id: cardType,
    value: cardType,
    title: card.title,
    icon: card.icon,
    color: cardColor, // Use the standard color if available, otherwise use the provided bgcolor
    bgIcon: bgIcon, // Use the appropriate background icon
  }

  return (
    <FeatureCard
      isHorizontal
      feature={featureOption}
      selected={selected}
      selectedFeatureId={selected ? featureOption.id : undefined}
      onClick={() => {
        onClick()
      }}
    />
  )
}

// Helper function to guess card type from title
function getCardTypeFromTitle(title: string): CardActivityType | 'BULK_CREATE' {
  const lowerTitle = title.toLowerCase()

  if (lowerTitle.includes('spoken') || (lowerTitle.includes('read') && lowerTitle.includes('respond'))) {
    return CardActivityType.READ_RESPOND
  }
  if (lowerTitle.includes('written') || lowerTitle.includes('write')) {
    return CardActivityType.RESPOND_WRITE
  }
  if (lowerTitle.includes('listen') || lowerTitle.includes('repeat')) {
    return CardActivityType.READ_REPEAT
  }
  if (lowerTitle.includes('bulk')) {
    return 'BULK_CREATE'
  }
  if (lowerTitle.includes('multiple') || lowerTitle.includes('choice')) {
    return CardActivityType.MULTIPLE_CHOICE
  }

  return 'BULK_CREATE' // Default fallback
}
